import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, ChevronLeft, ChevronDown, ChevronRight, Send, MessagesSquare, ExternalLink, Loader2, Car, X, Settings, Lock, Repeat, Activity, Video } from 'lucide-react';
import { autoAlertsAPI, vehicleLeadsAPI, vehiclePublicAPI, salesDeskAPI, notifyAPI } from '../lib/api';
import { fmtNumber } from '../lib/financeMath';
import { getStoredUser } from '../lib/session';
import VehicleShowroom from '../components/showroom/VehicleShowroom';
import SmsNotifyControl from '../components/notify/SmsNotifyControl';
import ActionHistoryPanel from '../components/shared/ActionHistoryPanel';
import BuyerActivityPanel from '../components/showroom/BuyerActivityPanel';
import { LiveNowBadge, useLiveBuyers } from '../components/showroom/LiveNow';
import LiveBuyersButton from '../components/showroom/LiveBuyersButton';
import { actionHistoryAPI } from '../lib/api';

const ORG = 'auto-demo-org-0001';
const POLL_MS = 10000;

const TYPE_KEY = {
    new_inquiry: 'newInquiry', buyer_replied: 'buyerReplied', high_intent: 'highIntent',
    follow_up: 'followUp', went_cold: 'wentCold', test_drive: 'testDrive', vehicle_interest: 'vehicleInterest',
    trade_in: 'tradeIn',
};

// Compact "time ago" for notification cards (trilingual, interpolated).
function relTime(iso, t) {
    try {
        const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
        if (s < 60) return t('salesDesk.timeNow', 'just now');
        const m = Math.floor(s / 60); if (m < 60) return t('salesDesk.timeMin', '{{n}}m ago', { n: m });
        const h = Math.floor(m / 60); if (h < 24) return t('salesDesk.timeHour', '{{n}}h ago', { n: h });
        return t('salesDesk.timeDay', '{{n}}d ago', { n: Math.floor(h / 24) });
    } catch { return ''; }
}

// Salesperson MOBILE experience: notification-first. Tap a notification to land
// on ONE focused inquiry that reuses the buyer's own VehicleShowroom, shows the
// buyer's message, and lets the salesperson reply inline. A "Manager" button
// opens a private side-chat that runs in parallel with the buyer conversation.
export default function SalesDesk() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    // The dealership's insurance broker gets the SAME desk, scoped server-side
    // to insurance conversations only (services/insurance_scope.py).
    // getStoredUser() returns the RAW JSON string, not an object.
    const isBroker = (() => {
        try { return !!JSON.parse(getStoredUser() || '{}').is_insurance_broker; } catch { return false; }
    })();
    const [alerts, setAlerts] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showRead, setShowRead] = useState(null);   // null = decide from the data
    const [search, setSearch] = useState('');
    // Board opens OPEN. A salesperson standing in front of a buyer must see the
    // work, not a collapsed header that reads as a broken screen. Same reason
    // the read group below auto-opens when there is nothing unread above it.
    const [showList, setShowList] = useState(true);
    const [openReq, setOpenReq] = useState(null);      // request_id of focused inquiry
    const [openAlert, setOpenAlert] = useState(null);  // full alert (carries stock/card_id + label)
    const [mgrOpen, setMgrOpen] = useState(false);
    const [mgrUnread, setMgrUnread] = useState(0);
    const [smsState, setSmsState] = useState(null);
    const [showSms, setShowSms] = useState(false);
    const reqIndex = useRef({});                        // request_id -> request (for card_id/vehicle)

    const loadAlerts = useCallback(async () => {
        try {
            const [al, reqs] = await Promise.all([
                autoAlertsAPI.list(ORG, 'salesperson'),
                vehicleLeadsAPI.salesRequests(ORG),
            ]);
            const list = (al.data?.alerts || []).slice().sort((a, b) => String(b.occurred_at || '').localeCompare(String(a.occurred_at || '')));
            setAlerts(list);
            setUnread(al.data?.unread || 0);
            const idx = {};
            (reqs.data?.requests || reqs.data || []).forEach((r) => { idx[r.id] = r; });
            reqIndex.current = idx;
        } catch { /* keep last */ }
        setLoading(false);
    }, []);

    const loadMgrUnread = useCallback(async () => {
        try { const r = await salesDeskAPI.managerChatUnread(ORG); setMgrUnread(r.data?.unread || 0); } catch { /* noop */ }
    }, []);

    useEffect(() => {
        loadAlerts(); loadMgrUnread();
        const id = setInterval(() => { if (!openReq) loadAlerts(); loadMgrUnread(); }, POLL_MS);
        return () => clearInterval(id);
    }, [loadAlerts, loadMgrUnread, openReq]);

    const loadSms = useCallback(async () => {
        try { const r = await notifyAPI.me(); setSmsState(r.data?.person || null); } catch { setSmsState(null); }
    }, []);
    useEffect(() => { loadSms(); }, [loadSms]);

    const saveSms = async (payload) => {
        try { const r = await notifyAPI.updateMe(payload); setSmsState(r.data?.person || null); } catch { /* keep last */ }
    };

    const openInquiry = useCallback(async (a) => {
        if (!a.request_id) {
            // A trade-in submitted outside a conversation has no thread to open;
            // acknowledge it rather than looking broken.
            try { await autoAlertsAPI.markRead(ORG, 'salesperson', [a.key]); loadAlerts(); } catch { /* noop */ }
            return;
        }
        setOpenReq(a.request_id);
        setOpenAlert(a);
        try { await autoAlertsAPI.markRead(ORG, 'salesperson', [a.key]); loadAlerts(); } catch { /* noop */ }
    }, [loadAlerts]);

    // "My ticket is TKT-…" — one box that matches the ticket, the buyer or
    // the vehicle. Filtering only; nothing about ownership changes.
    const needle = search.trim().toUpperCase();
    const matches = (a) => !needle || [a.ticket_code, a.buyer, a.vehicle, a.stock]
        .filter(Boolean).some((f) => String(f).toUpperCase().includes(needle));
    const unreadAlerts = alerts.filter((a) => !a.read && matches(a));
    const readAlerts = alerts.filter((a) => a.read && matches(a));
    // Which of these buyers is on the page RIGHT NOW. One call per tick for the
    // whole board — the badge is the notification, no hunting required.
    const liveIds = useLiveBuyers(ORG);

    // `?live=1` — the split-screen right pane. It only pre-arms the live feed
    // for a conversation the user opens themselves. Nothing opens on its own:
    // an auto-opened panel covered the pane's own controls (Cameras, Back).
    const autoLive = useRef(new URLSearchParams(window.location.search).get('live') === '1');
    // With nothing unread, a collapsed "Earlier" group leaves a blank board and
    // the salesperson concludes there is no work. Open it for them.
    const earlierOpen = showRead === null ? unreadAlerts.length === 0 : showRead;

    if (openReq) {
        return <FocusedInquiry reqId={openReq} request={reqIndex.current[openReq]} alertStock={openAlert?.stock} alertLabel={openAlert?.vehicle}
            live={liveIds.has(openReq)} autoGoLive={autoLive.current}
            onBack={() => { setOpenReq(null); setOpenAlert(null); loadAlerts(); }}
            onManager={isBroker ? null : () => setMgrOpen(true)} mgrUnread={mgrUnread}
            mgrDrawer={mgrOpen && !isBroker ? <ManagerChat onClose={() => { setMgrOpen(false); loadMgrUnread(); }} /> : null} />;
    }

    return (
        <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="sales-desk">
            <div className="max-w-md mx-auto px-4 py-4">
                <header className="flex items-center justify-between gap-2 mb-4">
                    <div className="flex items-center gap-2 min-w-0 shrink-0">
                        <span className="relative inline-flex">
                            {isBroker ? <Lock className="w-6 h-6 text-sky-400" /> : <Bell className="w-6 h-6 text-amber-400" />}
                            {unread > 0 && <span data-testid="sales-desk-unread-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>}
                        </span>
                        <h1 className="text-sm sm:text-base font-bold text-white whitespace-nowrap"
                            title={isBroker ? t('salesDesk.insuranceTitle', 'Insurance Desk') : t('salesDesk.title', 'Sales Desk')}
                            data-testid="sales-desk-title">
                            {isBroker ? t('salesDesk.insuranceTitle', 'Insurance Desk') : t('salesDesk.title', 'Sales Desk')}
                        </h1>
                    </div>
                    {/* Actions keep their own row space and may wrap as a group,
                        so a long FR/ES title never pushes them off screen. */}
                    <div className="flex items-center justify-end gap-2 flex-wrap min-w-0">
                        {/* STANDALONE Buyer Activity — works from the buyer's own
                            session, so someone who has only tapped a card is
                            reachable without a conversation existing first. */}
                        {!isBroker && <LiveBuyersButton organizationId={ORG} />}
                        {/* Lot camera — ALWAYS rendered. No notification, ticket,
                            data or flag condition: the lot feed is a permanent
                            entry point, and it opens the SAME route the Command
                            Center uses. Icon-only with a tooltip + aria-label so
                            the header never wraps. */}
                        {/* `from` tells the camera view where Back belongs. A
                            salesperson has no Command Center access, so sending
                            them there is a dead end. */}
                        <button onClick={() => navigate('/command-center/cameras?scope=lot&from=sales-desk')}
                            data-testid="sales-desk-lot-camera-btn"
                            title={t('salesDesk.lotCamera', 'Lot camera')}
                            aria-label={t('salesDesk.lotCamera', 'Lot camera')}
                            className="inline-flex shrink-0 items-center justify-center w-8 h-8 rounded-full border border-white/15 text-sky-400 hover:text-sky-300 hover:border-sky-400/60 transition-colors">
                            <Video className="w-4 h-4" />
                        </button>
                        <button onClick={() => setShowSms((s) => !s)} data-testid="sales-desk-sms-settings-btn"
                            aria-label={t('notify.myTitle', 'My notifications')}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors ${showSms ? 'border-amber-400/60 text-amber-300' : 'border-white/15 text-slate-300 hover:text-white'}`}>
                            <Settings className="w-4 h-4" />
                        </button>
                        {!isBroker && (
                            <button onClick={() => setMgrOpen(true)} data-testid="sales-desk-manager-btn"
                                className="relative inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-orange-600 hover:bg-orange-500 rounded-full px-3 py-1.5 transition-colors">
                                <MessagesSquare className="w-4 h-4" />{t('salesDesk.manager', 'Manager')}
                                {mgrUnread > 0 && <span data-testid="sales-desk-mgr-unread" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">{mgrUnread}</span>}
                            </button>
                        )}
                        <button onClick={() => navigate('/vehicle-leads')} data-testid="sales-desk-full-portal"
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-300 hover:text-white border border-white/15 rounded-full px-3 py-1.5 transition-colors">
                            <ExternalLink className="w-3.5 h-3.5" />{t('salesDesk.fullPortal', 'Full Portal')}
                        </button>
                    </div>
                </header>

                {/* Ticket / buyer / vehicle search — a buyer says "my ticket is
                    TKT-…" and the desk finds the thread instantly. */}
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('salesDesk.searchPlaceholder', 'Search ticket, buyer or vehicle')}
                    data-testid="sales-desk-search"
                    className="mb-4 w-full rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-amber-400/50 focus:outline-none" />

                {isBroker && (
                    <div className="mb-4 rounded-xl border border-sky-500/30 bg-sky-500/10 p-3 flex items-start gap-2"
                        data-testid="sales-desk-broker-banner">
                        <Lock className="w-4 h-4 text-sky-300 mt-0.5 shrink-0" />
                        <p className="text-xs text-sky-100 leading-relaxed">
                            {t('salesDesk.brokerBanner', 'Insurance conversations only. Nobody at the dealership can read these threads — the sales team is told a quote was requested, nothing more.')}
                        </p>
                    </div>
                )}

                {showSms && (
                    <div className="mb-4 space-y-3" data-testid="sales-desk-sms-settings">
                        <ActionHistoryPanel
                            testid="sales-action-history"
                            api={{
                                list: () => actionHistoryAPI.list(),
                                dismiss: (ids) => actionHistoryAPI.dismiss(null, ids),
                                clear: () => actionHistoryAPI.clear(),
                            }}
                            labels={{
                                title: t('actionHistory.title', 'Recent actions'),
                                empty: t('actionHistory.empty', 'Nothing yet — your recent actions will appear here.'),
                                clearAll: t('actionHistory.clearAll', 'Clear all'),
                                remove: t('actionHistory.remove', 'Remove from my list'),
                                footnote: t('actionHistory.footnote', 'This is your own list. Removing an item never changes the record.'),
                                action: {
                                    acknowledge: t('actionHistory.action.acknowledge', 'Acknowledged'),
                                    resolve: t('actionHistory.action.resolve', 'Resolved'),
                                    cancel: t('actionHistory.action.cancel', 'Cancelled'),
                                    confirm: t('actionHistory.action.confirm', 'Confirmed'),
                                    decline: t('actionHistory.action.decline', 'Declined'),
                                    complete: t('actionHistory.action.complete', 'Completed'),
                                    authorize: t('actionHistory.action.authorize', 'Authorised'),
                                    revoke: t('actionHistory.action.revoke', 'Withdrawn'),
                                },
                            }}
                        />
                        {smsState ? (
                            <SmsNotifyControl state={smsState} onSave={saveSms} testid="sales-desk-sms"
                                labels={{
                                    title: t('notify.myTitle', 'My notifications'),
                                    sendTo: t('notify.sendTo', 'Texts go to'),
                                    noPhone: t('notify.noPhone', 'No phone number on your profile yet — ask your manager to add one.'),
                                    smsOn: t('notify.on', 'On'),
                                    smsOff: t('notify.off', 'Off'),
                                    pauseTitle: t('notify.pauseTitle', 'Pause for'),
                                    pause2h: t('notify.pause2h', '2 hours'),
                                    pauseTomorrow: t('notify.pauseTomorrow', 'Until tomorrow 9:00'),
                                    pause2d: t('notify.pause2d', '2 days'),
                                    pausedUntil: t('notify.pausedUntil', 'Paused until {{when}}'),
                                    resumeNow: t('notify.resumeNow', 'Turn back on'),
                                }} />
                        ) : (
                            <p className="text-xs text-slate-400" data-testid="sales-desk-sms-none">{t('notify.noPersonRecord', 'No staff profile is linked to this login yet.')}</p>
                        )}
                    </div>
                )}

                <button onClick={() => setShowList((s) => !s)} data-testid="sales-desk-list-toggle"
                    className="w-full flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-slate-400 hover:text-slate-200 mb-2 transition-colors">
                    {showList ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    {t('salesDesk.notifications', 'Notifications')}{unread > 0 ? ` (${unread})` : ''}
                </button>

                {showList && (loading ? (
                    <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" />{t('salesDesk.loading', 'Loading…')}</div>
                ) : (
                    <div className="space-y-2" data-testid="sales-desk-list">
                        {/* The tap IS the notification. These rows appear seconds
                            after a card is tapped, before any message exists, and
                            open that exact buyer's live feed. */}
                        {!isBroker && (
                            <LiveBuyersButton inline organizationId={ORG}
                                autoGoLive={autoLive.current} />
                        )}
                        {unreadAlerts.length === 0 && readAlerts.length === 0 && (
                            <p className="text-sm text-slate-400 py-8 text-center" data-testid="sales-desk-empty">{t('salesDesk.empty', 'No notifications right now')}</p>
                        )}
                        {unreadAlerts.map((a) => <AlertCard key={a.key} a={a} onOpen={openInquiry} t={t} live={liveIds.has(a.request_id)} />)}

                        {readAlerts.length > 0 && (
                            <>
                                <button onClick={() => setShowRead(!earlierOpen)} data-testid="sales-desk-earlier-toggle"
                                    className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-slate-200 mt-4 mb-1 transition-colors">
                                    {earlierOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    {t('salesDesk.earlier', 'Earlier')} ({readAlerts.length})
                                </button>
                                {earlierOpen && <div className="space-y-2" data-testid="sales-desk-earlier-list">{readAlerts.map((a) => <AlertCard key={a.key} a={a} onOpen={openInquiry} t={t} live={liveIds.has(a.request_id)} />)}</div>}
                            </>
                        )}
                    </div>
                ))}
            </div>
            {mgrOpen && <ManagerChat onClose={() => { setMgrOpen(false); loadMgrUnread(); }} />}
        </div>
    );
}

function AlertCard({ a, onOpen, t, live = false }) {
    const label = t(`salesDesk.type.${TYPE_KEY[a.type] || 'newInquiry'}`, a.type.replace(/_/g, ' '));
    const vehicle = [a.year, a.make, a.model].filter(Boolean).join(' ') || a.vehicle || '';
    const preview = (a.message || '').trim();
    const when = a.occurred_at ? relTime(a.occurred_at, t) : '';
    return (
        <button onClick={() => onOpen(a)} data-testid={`sales-desk-alert-${a.request_id || a.key}`}
            className={`w-full text-left rounded-2xl border p-3.5 flex gap-3 items-start transition-all hover:-translate-y-0.5 hover:shadow-lg ${a.read ? 'border-white/10 bg-white/[0.02]' : 'border-amber-500/50 bg-amber-500/[0.07] hover:bg-amber-500/[0.12]'}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className={`text-[11px] font-bold uppercase tracking-wide ${a.read ? 'text-slate-400' : 'text-amber-300'}`}>{label}</span>
                    {!a.read && <span className="w-2.5 h-2.5 rounded-full bg-rose-500 shrink-0 shadow-[0_0_0_3px_rgba(244,63,94,0.2)]" data-testid="sales-desk-alert-unread-dot" />}
                    {live && <LiveNowBadge testid={`sales-desk-alert-live-${a.request_id || a.key}`} />}
                    {when && <span className="ml-auto text-[11px] text-slate-500 shrink-0">{when}</span>}
                </div>
                <p className="text-sm font-semibold text-white mt-1 truncate">{a.buyer || t('salesDesk.aBuyer', 'A buyer')}</p>
                {(a.ticket_code || a.returning_buyer) && (
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                        {a.returning_buyer && (
                            <span className="inline-block rounded-md border border-sky-500/30 bg-sky-500/10 px-1.5 py-0.5 text-[11px] font-semibold tracking-wide text-sky-300"
                                data-testid={`sales-desk-returning-${a.request_id || a.key}`}>
                                {t('salesDesk.returningBuyer', 'Returning buyer')}
                            </span>
                        )}
                        {a.ticket_code && (
                            <span className="inline-block rounded-md border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[11px] font-semibold tracking-wide text-emerald-300"
                                data-testid={`sales-desk-ticket-${a.request_id || a.key}`}>{a.ticket_code}</span>
                        )}
                        {a.claim_reopened && a.claim_reopened_from && (
                            <span className="inline-block rounded-md border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[11px] font-semibold tracking-wide text-amber-300"
                                data-testid={`sales-desk-reopened-${a.request_id || a.key}`}>
                                {t('salesDesk.claimReopened', 'Reopened from {{name}}', { name: a.claim_reopened_from })}
                            </span>
                        )}
                    </div>
                )}
                {vehicle && <p className="text-xs text-slate-300 mt-0.5 flex items-center gap-1 truncate"><Car className="w-3 h-3 shrink-0 text-slate-400" />{vehicle}</p>}
                {a.type === 'trade_in' ? (
                    <p className="text-xs text-sky-300 mt-1 flex items-center gap-1 truncate" data-testid="sales-desk-alert-trade">
                        <Repeat className="w-3 h-3 shrink-0" />
                        {[a.trade_label,
                          a.mileage ? `${fmtNumber(a.mileage)} km` : null,
                          a.condition,
                          a.photos ? (a.photos === 1 ? t('salesDesk.tradePhoto', '1 photo')
                                                    : t('salesDesk.tradePhotos', '{{n}} photos', { n: a.photos })) : null,
                         ].filter(Boolean).join(' · ')}
                    </p>
                ) : null}
                {preview && <p className="text-sm text-slate-300 mt-1.5 line-clamp-2">“{preview}”</p>}
            </div>
            <ChevronRight className="w-5 h-5 text-slate-500 shrink-0 self-center" />
        </button>
    );
}

function FocusedInquiry({ reqId, request, alertStock, alertLabel, onBack, onManager, mgrUnread, mgrDrawer, live = false, autoGoLive = false }) {
    const { t } = useTranslation();
    const [veh, setVeh] = useState(null);
    const [vehMeta, setVehMeta] = useState({});
    const [thread, setThread] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activityOpen, setActivityOpen] = useState(autoGoLive);
    const cardId = request?.card_id || request?.stock_number || alertStock;
    const bottomRef = useRef(null);

    const loadThread = useCallback(async () => {
        try { const r = await vehicleLeadsAPI.thread(reqId); setThread(r.data?.messages || []); } catch { /* noop */ }
    }, [reqId]);

    useEffect(() => {
        (async () => {
            if (cardId) { try { const r = await vehiclePublicAPI.get(cardId); setVeh(r.data?.vehicle || null); setVehMeta({ dealership: r.data?.dealership, salesStatus: r.data?.sales_status }); } catch { /* noop */ } }
            await loadThread(); setLoading(false);
        })();
        const id = setInterval(loadThread, POLL_MS);
        return () => clearInterval(id);
    }, [cardId, loadThread]);

    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread]);

    const send = async () => {
        const body = input.trim();
        if (!body) return;
        setSending(true);
        try { await vehicleLeadsAPI.reply(reqId, { body }); setInput(''); await loadThread(); } catch { /* noop */ }
        setSending(false);
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="sales-desk-inquiry">
            <div className="max-w-md mx-auto pb-40">
                <header className="sticky top-0 z-20 flex items-center justify-between px-3 py-2.5 bg-slate-950/90 backdrop-blur border-b border-white/10">
                    <button onClick={onBack} data-testid="inquiry-back" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white transition-colors">
                        <ChevronLeft className="w-5 h-5" />{t('salesDesk.back', 'Back')}
                    </button>
                    <div className="flex items-center gap-1.5">
                    <button onClick={() => setActivityOpen(true)} data-testid="inquiry-buyer-activity-btn"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-100 bg-emerald-600/80 hover:bg-emerald-500 rounded-full px-3 py-1.5 transition-colors">
                        <Activity className="w-4 h-4" />{t('buyerActivity.button', 'Buyer Activity')}
                    </button>
                    {live && <LiveNowBadge testid="inquiry-live-badge" />}
                    {onManager ? (
                        <button onClick={onManager} data-testid="inquiry-manager-btn"
                            className="relative inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-orange-600 hover:bg-orange-500 rounded-full px-3 py-1.5 transition-colors">
                            <MessagesSquare className="w-4 h-4" />{t('salesDesk.askManager', 'Ask Manager')}
                            {mgrUnread > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">{mgrUnread}</span>}
                        </button>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-200" data-testid="inquiry-confidential">
                            <Lock className="w-3.5 h-3.5" />{t('salesDesk.confidential', 'Confidential')}
                        </span>
                    )}
                    </div>
                </header>
                <BuyerActivityPanel conversationId={reqId} open={activityOpen} autoGoLive={autoGoLive}
                    onClose={() => setActivityOpen(false)} />

                {loading ? (
                    <div className="flex items-center gap-2 text-slate-400 text-sm py-16 justify-center"><Loader2 className="w-4 h-4 animate-spin" />{t('salesDesk.loading', 'Loading…')}</div>
                ) : (
                    <>
                        {/* SAME vehicle presentation the buyer sees */}
                        {veh ? <VehicleShowroom vehicle={veh} dealership={vehMeta.dealership} salesStatus={vehMeta.salesStatus} preview /> : (
                            <p className="text-sm text-slate-400 px-4 py-6">{alertLabel || t('salesDesk.vehicleUnavailable', 'Vehicle details unavailable')}</p>
                        )}

                        <div className="px-4 mt-4">
                            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">{t('salesDesk.conversation', 'Conversation with the buyer')}</p>
                            <div className="space-y-2" data-testid="inquiry-thread">
                                {thread.length === 0 && <p className="text-sm text-slate-500">{t('salesDesk.noMessages', 'No messages yet')}</p>}
                                {thread.map((m) => {
                                    const mine = m.sender_role === 'sales' || m.sender_role === 'manager';
                                    return (
                                        <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                                            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? 'bg-blue-600 text-white' : 'bg-white/10 text-slate-100'}`}>
                                                <p className={`text-[10px] font-semibold mb-0.5 ${mine ? 'text-blue-100' : 'text-amber-300'}`}>{mine ? (m.sender_name || t('salesDesk.you', 'You')) : (m.sender_name || t('salesDesk.buyer', 'Buyer'))}</p>
                                                <p className="leading-snug">{m.body}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={bottomRef} />
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Reply box (buyer-facing) — lifted above the demo switcher so it never hides it */}
            <div className={`fixed left-0 right-0 z-20 bg-slate-950/95 backdrop-blur border-t border-white/10 ${typeof window !== 'undefined' && sessionStorage.getItem('yulontap_auto_demo') === '1' ? 'bottom-16' : 'bottom-0'}`}>
                <div className="max-w-md mx-auto px-3 py-3 flex items-center gap-2">
                    <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                        data-testid="inquiry-reply-input" placeholder={t('salesDesk.replyPlaceholder', 'Reply to the buyer…')}
                        className="flex-1 bg-white/10 text-white placeholder:text-slate-500 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                    <button onClick={send} disabled={sending || !input.trim()} data-testid="inquiry-reply-send"
                        className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white transition-colors">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {mgrDrawer}
        </div>
    );
}

function ManagerChat({ onClose }) {
    const { t } = useTranslation();
    const [msgs, setMsgs] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const bottomRef = useRef(null);

    const load = useCallback(async () => {
        try { const r = await salesDeskAPI.managerChat(ORG); setMsgs(r.data?.messages || []); } catch { /* noop */ }
    }, []);
    useEffect(() => { load(); const id = setInterval(load, POLL_MS); return () => clearInterval(id); }, [load]);
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);

    const send = async () => {
        const body = input.trim();
        if (!body) return;
        setSending(true);
        try { await salesDeskAPI.sendManagerChat(ORG, body); setInput(''); await load(); } catch { /* noop */ }
        setSending(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="manager-chat">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} data-testid="manager-chat-overlay" />
            <div className="relative w-full max-w-md h-full bg-slate-900 border-l border-white/10 flex flex-col">
                <header className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <MessagesSquare className="w-5 h-5 text-orange-400" />
                        <h2 className="text-base font-bold text-white">{t('salesDesk.managerChatTitle', 'Manager')}</h2>
                    </div>
                    <button onClick={onClose} data-testid="manager-chat-close" className="text-slate-400 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
                </header>
                <p className="px-4 pt-2 text-[11px] text-slate-400">{t('salesDesk.managerChatHint', 'Private chat with your manager — the buyer never sees this.')}</p>
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2" data-testid="manager-chat-messages">
                    {msgs.length === 0 && <p className="text-sm text-slate-500">{t('salesDesk.noMessages', 'No messages yet')}</p>}
                    {msgs.map((m) => {
                        const mine = m.sender_role === 'salesperson';
                        return (
                            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? 'bg-blue-600 text-white' : 'bg-orange-600/90 text-white'}`}>
                                    <p className="text-[10px] font-semibold mb-0.5 text-white/80">{m.sender_role === 'salesperson' ? t('salesDesk.roleSalesperson', 'Salesperson') : t('salesDesk.roleManager', 'Manager')}</p>
                                    <p className="leading-snug">{m.body}</p>
                                </div>
                            </div>
                        );
                    })}
                    <div ref={bottomRef} />
                </div>
                <div className="px-3 py-3 border-t border-white/10 flex items-center gap-2">
                    <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                        data-testid="manager-chat-input" placeholder={t('salesDesk.managerPlaceholder', 'Ask your manager…')}
                        className="flex-1 bg-white/10 text-white placeholder:text-slate-500 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500" />
                    <button onClick={send} disabled={sending || !input.trim()} data-testid="manager-chat-send"
                        className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white transition-colors">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                </div>
            </div>
        </div>
    );
}
